<template>
	<div class="graph-nd graph-2d">
		<svg :viewBox="'0 0 ' + width + ' ' + height" xmlns="http://www.w3.org/2000/svg">
			<Legend v-bind:width="width"
				v-bind:height="height"
				v-bind:startDate="startDate"
				v-bind:bins="bins.length"
				v-bind:binWidth="binWidth">
			</Legend>

			<g v-for="(bin, index) in bins" :key="index"
				class="tooltip-trigger"
				@click="showTooltip(bin, ...arguments)"
				@mouseover="showTooltip(bin, ...arguments)"
				@mouseout="hideTooltip">

				<template v-if="bin.length == 1">
					<g v-for="valueIndex in [1, 0]" :key="valueIndex">
						<text :x="centerScaleX(index) + 22"
							  :y="scaleYOffset(bin[0].value, valueIndex)"
							  :style="style.labelFont"
		  					  text-anchor="middle">
							  {{ bin[0].value[valueIndex] }}
						</text>
						<circle :cx="centerScaleX(index)"
								:cy="scaleY(bin[0].value[valueIndex], valueIndex)"
								:r="style.pointRadius / 2 + 2"
								:fill="style.pointBackgroundColor">
						</circle>
						<circle :cx="centerScaleX(index)"
								:cy="scaleY(bin[0].value[valueIndex], valueIndex)"
								:r="style.pointRadius / 2"
								:fill="(valueIndex == 0) ? style.pointColor : style.pointGrayColor">
						</circle>
					</g>
				</template>

				<template v-else-if="bin.length > 1">
					<g v-for="valueIndex in [1, 0]" :key="valueIndex">
						<template v-if="binMax(bin, valueIndex) != binMin(bin, valueIndex)">
							<text :x="centerScaleX(index) + 22"
								  :y="scaleYOffset([ binMax(bin, valueIndex), binMin(bin, valueIndex) ], 0) + 4"
								  :style="style.labelFont"
			  					  text-anchor="middle">
								  {{ binMax(bin, valueIndex) }}
							</text>
							<text :x="centerScaleX(index) + 22"
								  :y="scaleYOffset([ binMax(bin, valueIndex), binMin(bin, valueIndex) ], 1) + 4"
								  :style="style.labelFont"
			  					  text-anchor="middle">
								  {{ binMin(bin, valueIndex) }}
							</text>
						</template>
						<template v-else>
							<text :x="centerScaleX(index) + 22"
								  :y="scaleY(binMax(bin, valueIndex))"
								  :style="style.labelFont"
			  					  text-anchor="middle">
								  {{ binMax(bin, valueIndex) }}
							</text>
						</template>
						<!-- <rect :x="centerScaleX(index) - style.pointRadius"
							  :y="scaleY(firstValue(bin))"
							  :rx="style.pointRadius"
							  :width="style.pointRadius * 2"
							  :height="Math.abs(scaleY(firstValue(bin)) - scaleY(lastValue(bin)))"
							  :fill="style.pointBackgroundColor">
						</rect> -->
						<rect :x="centerScaleX(index) - style.pointRadius / 2"
							  :y="scaleY(binMax(bin, valueIndex)) + style.pointRadius / 2"
							  :rx="style.pointRadius / 2"
							  :width="style.pointRadius"
							  :height="Math.max(Math.abs(scaleY(binMax(bin, valueIndex)) - scaleY(binMin(bin, valueIndex))) - style.pointRadius, style.pointRadius)"
							  :fill="(valueIndex == 0) ? style.pointColor : style.pointGrayColor">
						</rect>

						<!-- <circle :cx="centerScaleX(index)"
								:cy="scaleY(bin[0].value[valueIndex], valueIndex)"
								:r="style.pointRadius / 2 + 2"
								:fill="style.pointBackgroundColor">
						</circle>
						<circle :cx="centerScaleX(index)"
								:cy="scaleY(bin[0].value[valueIndex], valueIndex)"
								:r="style.pointRadius / 2"
								:fill="(valueIndex == 0) ? style.pointColor : style.pointGrayColor">
						</circle> -->
					</g>
				</template>
			</g>
		</svg>

		<!-- Place the tooltip here -->
		<Tooltip v-bind:display="tooltipDisplay" @hover="hoverTooltip">
			<p v-for="(point, i) in tooltipBin" :key="i">
				<strong>{{ withPrecision(point.value[0]) }} / {{ withPrecision(point.value[1]) }}</strong> (mm/Hg)<br>
				<small>{{ formatDateTime(point.time) }}</small>
			</p>
		</Tooltip>
	</div>
</template>

<script>
import Graph from '.'

export default {
	name: 'graph-3d',
	extends: Graph,

	props: {
		precision: {
			type: Number,
			default: 0
		}
	},

	computed: {
		/**
		 * @function 	bins
		 * @desc 		Bins the data points into day-wide bins for the interval determined by the current scale
		 */
		bins() {
			var bins = []

			for (var t = this.startDate.getTime() ; t <= this.date.getTime() ; t += this.binInterval) {
				bins.push(this.data.filter((point) =>
					(t <= point.time && point.time <= t + this.binInterval)
				).sort((a, b) => {
					return b.value - a.value
				}))
			}

			return bins
		},

		/**
		 * @function 	visibleData
		 * @desc 		'data' (passed as a prop) lying in the date range
		 */
		visibleData() {
			var s = this.startDate.getTime(),
				e = this.date.getTime()
			return this.data.filter((point) => (s <= point.time && point.time <= e))
		},

		/**
		 * @function 	maxValue
		 * @desc 		maximum value among all values in 'visibleData'
		 */
		maxValue() {
			return [
				Math.max.apply(null, this.visibleData.map((point) => point.value[0])),
				Math.max.apply(null, this.visibleData.map((point) => point.value[1]))
			]
		},

		/**
		 * @function 	minValue
		 * @desc 		minimum value among all values in 'visibleData'
		 */
		minValue() {
			return [
				Math.min.apply(null, this.visibleData.map((point) => point.value[0])),
				Math.min.apply(null, this.visibleData.map((point) => point.value[1]))
			]
		},

		binMax() {
			return (bin, index) => {
				return Math.max.apply(null, bin.map((point) => point.value[index]))
			}
		},

		binMin() {
			return (bin, index) => {
				return Math.min.apply(null, bin.map((point) => point.value[index]))
			}
		},

		globalMaxValue() {
			return Math.max.apply(null, this.maxValue)
		},

		globalMinValue() {
			return Math.min.apply(null, this.minValue)
		},

		globalRange() {
			return Math.abs(this.globalMaxValue - this.globalMinValue)
		},

		range() {
			return [
				this.maxValue[0] - this.minValue[0],
				this.maxValue[1] - this.minValue[1]
			]
		},
		displayHeight() {
			return this.height - this.style.padding * 2 - this.style.legendHeight - 60
		},
		legendStart() {
			return this.height - this.style.padding * 2 - this.style.legendHeight
		},
		interval() {
			return this.date.getTime() - this.startDate.getTime()
		}
	},
	methods: {
		setScale(scale) {
			this.scale = scale
		},
		scaleX(x) {
			return x * this.width / this.bins.length
		},
		scaleY(y, i) {
			if (this.globalRange == 0)
				return (this.height - 35) / 2 + ((i == 1) ? -20 : 20)
			return this.legendStart - ((y - this.globalMinValue) / this.globalRange) * this.displayHeight - 30
		},

		/**
		 * @function 	scaleYOffset
		 * @desc 		rounding the given value w.r.t the precision
		 * @param 		{value, i} Number
		 */
		scaleYOffset(value, i) {
			var sy = [ this.scaleY(value[0], 0), this.scaleY(value[1], 1)]
			var scaleDifference = Math.abs(sy[0] - sy[1])

			if (scaleDifference <= this.style.labelFont.fontSize) {
				return sy[i] + (((i == 0) ? -1 : 1) * this.style.labelFont.fontSize / 2)
			}
			else return sy[i]
		},
		centerScaleX(x) {
			return (x + 0.5) * this.width / (this.bins.length + 1)
		},

		/**
		 * @function 	withPrecision
		 * @desc 		rounding the given value w.r.t the precision
		 * @param 		{value} Number
		 */
		withPrecision(value) {
			if (this.precision == 0 || Math.abs(value - Math.round(value)) < 0.01) {
				return Math.round(value)
			}
			else {
				return value.toFixed(this.precision)
			}
		}
  	}
}
</script>

<style lang="scss" scoped>
.graph-2d {
	background-color: #ffffff;
	position: relative;
}
svg {
	width: 100%;
	height: auto;
}
</style>
